/* =============================================================
   SiteHeader — the prajnainnovates.com masthead, shared by every app
   on app.prajnainnovates.com (the Interaction Map renders the same
   markup in interaction-map/index.html; styles in site-header.css).

   Site name · "/ App name  Beta" for the app you're in · Resource hub ·
   Tools (all apps, same tab) · Profile · account, as on the main site.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AccountMenu } from './AccountMenu';
import { ASSESSMENT, CHECKLIST, MAP } from '../lib/routes';
import { SITE_URL } from '../lib/nav';

const APPS = [
  {
    id: 'assessment',
    name: 'Psychological Safety Assessment',
    tagline: 'A 143-item reflection on the everyday practice of psychological safety.',
    href: ASSESSMENT,
    stage: 'Pre-beta',
  },
  {
    id: 'checklist',
    name: 'CYBHI Onboarding Checklist',
    tagline: "An LEA's own program compliance checklist.",
    href: CHECKLIST,
    stage: 'Beta',
  },
  {
    id: 'map',
    name: 'Interaction Map',
    tagline: 'Map interactions between entities, departments, stakeholders, process steps.',
    href: MAP,
    stage: 'Beta',
  },
];

/* Which app (if any) the visitor is in, from the path. */
function current(pathname: string) {
  if (pathname.startsWith(ASSESSMENT)) return { app: APPS[0], label: APPS[0].name, home: ASSESSMENT, stage: APPS[0].stage };
  if (pathname.startsWith(CHECKLIST)) return { app: APPS[1], label: APPS[1].name, home: CHECKLIST, stage: APPS[1].stage };
  if (pathname.startsWith('/account') || pathname.startsWith('/auth/'))
    return { app: null, label: 'Your account', home: '/account', stage: null };
  return null;
}

export function SiteHeader() {
  const { pathname } = useLocation();
  const here = current(pathname);
  const [open, setOpen] = useState(false);
  const dd = useRef<HTMLDivElement>(null);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (here) document.title = `${here.label} — Prajna Innovates`;
    else document.title = 'Prajna Innovates — Apps';
  }, [here?.label]);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !dd.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  return (
    <header className="pi-mast">
      <div className="pi-in">
        <div className="pi-brand">
          <a href={`${SITE_URL}/`} className="pi-name">
            Ashutosh "Ash" Pandey<em> · notes from the field</em>
          </a>
          {here && (
            <>
              <span className="pi-slash" aria-hidden="true">/</span>
              <Link to={here.home} className="pi-app">
                {here.label}
              </Link>
              {here.stage && (
                <span className="pi-beta pi-beta-main" title={`${here.label} is in ${here.stage.toLowerCase()}: it works, but features may change.`}>
                  {here.stage}
                </span>
              )}
            </>
          )}
        </div>
        <nav className="pi-nav" aria-label="Site">
          <a href={`${SITE_URL}/notes.html`}>Resource hub</a>
          <div className={`pi-dd${open ? ' open' : ''}`} ref={dd}>
            <button
              type="button"
              className={here?.app ? 'pi-on' : ''}
              aria-haspopup="true"
              aria-expanded={open}
              onClick={(e) => {
                e.stopPropagation();
                setOpen((o) => !o);
              }}
            >
              Tools <span className="pi-chev" aria-hidden="true" />
            </button>
            <div className="pi-panel" role="menu">
              {APPS.map((a) => {
                const cur = here?.app?.id === a.id;
                const body = (
                  <>
                    <div className="pi-ih">
                      <div className="pi-in-name">
                        {a.name}
                        <span className="pi-beta">{a.stage}</span>
                      </div>
                      {cur && <div className="pi-ext">You are here</div>}
                    </div>
                    <div className="pi-tag">{a.tagline}</div>
                  </>
                );
                /* The map is its own page, so it needs a full page load. */
                return a.id === 'map' ? (
                  <a key={a.id} href={a.href} className="pi-item" role="menuitem">{body}</a>
                ) : (
                  <Link key={a.id} to={a.href} className={`pi-item${cur ? ' pi-cur' : ''}`} role="menuitem" aria-current={cur ? 'page' : undefined}>
                    {body}
                  </Link>
                );
              })}
            </div>
          </div>
          <a href={`${SITE_URL}/about.html`}>Profile</a>
          <AccountMenu />
        </nav>
      </div>
    </header>
  );
}
